import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import UserMenu from '../components/UserMenu';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { 
  Zap, Moon, Sun, CreditCard, Calendar, CheckCircle, 
  AlertCircle, ChevronRight, Crown, Sparkles, Shield, XCircle, Loader2, Gift, Infinity
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme, toggleTheme } = useTheme();
  const { user, token, loading: authLoading, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Track if we've already processed the session
  const processedSessionRef = useRef(false);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user) {
      navigate('/auth?redirect=/subscription');
      return;
    }
    
    // Check if returning from Stripe checkout
    const sessionId = searchParams.get('session_id');
    if (sessionId && !processedSessionRef.current) {
      processedSessionRef.current = true; // Mark as processed to prevent infinite loop
      checkPaymentStatus(sessionId);
    } else if (!sessionId) {
      fetchSubscription();
    }
  }, [user, authLoading, navigate, searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await axios.get(
          `${API}/api/billing/checkout/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.payment_status === 'paid') {
          // Clear the session_id from URL first
          window.history.replaceState({}, '', '/subscription');
          // Stop showing verification screen
          setCheckingPayment(false);
          // Show success toast
          toast.success(`Successfully upgraded to ${response.data.plan}!`);
          // Refresh user data to get updated subscription
          await refreshUser();
          fetchSubscription();
          return;
        } else if (response.data.status === 'expired') {
          setCheckingPayment(false);
          toast.error('Payment session expired');
          window.history.replaceState({}, '', '/subscription');
          fetchSubscription();
          return;
        }

        // Keep polling
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setCheckingPayment(false);
          toast.info('Payment processing. Check back shortly.');
          window.history.replaceState({}, '', '/subscription');
          // Still refresh user in case payment went through
          await refreshUser();
          fetchSubscription();
        }
      } catch (error) {
        console.error('Payment status check failed:', error);
        setCheckingPayment(false);
        toast.error('Failed to verify payment');
        window.history.replaceState({}, '', '/subscription');
        fetchSubscription();
      }
    };

    poll();
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/api/billing/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sub = response.data.subscription;
      
      // If subscription is paid but has missing/incorrect dates, try to fix them
      if (sub && sub.tier !== 'free' && (!sub.trial_end || !sub.current_period_end)) {
        try {
          await axios.post(`${API}/api/billing/fix-subscription`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Re-fetch after fix
          const fixedResponse = await axios.get(`${API}/api/billing/subscription`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSubscription(fixedResponse.data.subscription);
          setUsage(fixedResponse.data.usage);
        } catch (fixError) {
          console.log('Could not auto-fix subscription dates');
          setSubscription(sub);
          setUsage(response.data.usage);
        }
      } else {
        setSubscription(sub);
        setUsage(response.data.usage);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      toast.error('Failed to load subscription');
    } finally {
      setLoading(false);
      setCheckingPayment(false);
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'free': return <Shield className="w-6 h-6" />;
      case 'basic': return <Sparkles className="w-6 h-6" />;
      case 'pro': return <Crown className="w-6 h-6" />;
      case 'lifetime_pro': return <Gift className="w-6 h-6" />;
      default: return <Shield className="w-6 h-6" />;
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'free': return 'text-muted-foreground bg-secondary';
      case 'basic': return 'text-blue-500 bg-blue-500/20';
      case 'pro': return 'text-primary bg-primary/20';
      case 'lifetime_pro': return 'text-amber-500 bg-amber-500/20';
      default: return 'text-muted-foreground bg-secondary';
    }
  };

  // Show loading while auth is checking
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-500 text-xs rounded-full">
            <CheckCircle className="w-3 h-3" />
            Active
          </span>
        );
      case 'trialing':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-500 text-xs rounded-full">
            <AlertCircle className="w-3 h-3" />
            Trial
          </span>
        );
      case 'past_due':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-500 text-xs rounded-full">
            <AlertCircle className="w-3 h-3" />
            Past Due
          </span>
        );
      case 'canceled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-500 text-xs rounded-full">
            <XCircle className="w-3 h-3" />
            Canceled
          </span>
        );
      default:
        return null;
    }
  };

  const formatLimit = (limit) => {
    if (limit === -1) return 'Unlimited';
    return limit.toString();
  };

  if (loading || checkingPayment) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        {checkingPayment && (
          <p className="text-muted-foreground">Verifying payment...</p>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <NavigationMenu />
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg">FlipValue</span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-secondary">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-6 max-w-2xl mx-auto w-full">
        <h1 className="text-2xl font-bold mb-6">Manage Subscription</h1>

        {/* Current Plan Card */}
        <div className="bg-card rounded-xl border border-border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${getTierColor(subscription?.tier)}`}>
                {getTierIcon(subscription?.tier)}
              </div>
              <div>
                <h2 className="text-xl font-bold">
                  {subscription?.tier === 'lifetime_pro' ? 'Lifetime Pro' : 
                   subscription?.tier === 'pro' ? 'Pro' :
                   subscription?.tier === 'basic' ? 'Basic' : 'Free'} Plan
                </h2>
                {getStatusBadge(subscription?.status)}
              </div>
            </div>
          </div>

          {subscription?.tier !== 'free' && (
            <div className="space-y-3 border-t border-border pt-4 mt-4">
              {/* Show trial info if on trial */}
              {subscription?.status === 'trialing' && subscription?.trial_end && (
                <div className="flex items-center gap-3 text-sm bg-blue-500/10 text-blue-500 p-3 rounded-lg">
                  <Sparkles className="w-4 h-4" />
                  <span>
                    Free trial ends {new Date(subscription.trial_end).toLocaleDateString()} • No charge until then
                  </span>
                </div>
              )}
              
              {/* Show first billing date (same as trial end) or renewal date */}
              {subscription?.status === 'trialing' && subscription?.trial_end ? (
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>
                    First billing date: {new Date(subscription.trial_end).toLocaleDateString()}
                  </span>
                </div>
              ) : subscription?.current_period_end && (
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>
                    {subscription.cancel_at_period_end ? 'Access ends' : 'Renews'} on{' '}
                    {new Date(subscription.current_period_end).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Usage Summary */}
        {usage && (
          <div className="bg-card rounded-xl border border-border p-6 mb-6">
            <h3 className="font-semibold mb-4">Current Usage</h3>
            <div className="grid grid-cols-2 gap-4">
              {/* Combined Actions (Searches + Calculations) for Free plan */}
              {subscription?.tier === 'free' ? (
                <div className="p-3 bg-secondary/30 rounded-lg col-span-2">
                  <p className="text-sm text-muted-foreground">Actions (Searches + Calculations)</p>
                  <p className="text-lg font-bold">
                    {(usage.scans_used || 0) + (usage.profit_calcs_used || 0)} / {formatLimit(usage.scans_limit)}
                  </p>
                </div>
              ) : (
                <>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-sm text-muted-foreground">Searches</p>
                    <p className="text-lg font-bold">
                      {usage.scans_used} / {formatLimit(usage.scans_limit)}
                    </p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-sm text-muted-foreground">Profit Calculations</p>
                    <p className="text-lg font-bold">
                      {usage.profit_calcs_used} / {formatLimit(usage.profit_calcs_limit)}
                    </p>
                  </div>
                </>
              )}
              <div className="p-3 bg-secondary/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Inventory</p>
                <p className="text-lg font-bold">
                  {usage.inventory_count} / {formatLimit(usage.inventory_limit)}
                </p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Releases</p>
                <p className="text-lg font-bold">
                  {usage.release_count} / {formatLimit(usage.release_limit)}
                </p>
              </div>
            </div>
            {subscription?.tier === 'free' ? (
              <p className="text-xs text-muted-foreground mt-3">
                Free plan: 50 lifetime actions, 10 inventory items max
              </p>
            ) : usage.reset_date && (
              <p className="text-xs text-muted-foreground mt-3">
                Actions reset on {new Date(usage.reset_date).toLocaleDateString()}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Inventory limit does not reset
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {subscription?.tier === 'free' && (
            <Button 
              onClick={() => navigate('/pricing')} 
              className="w-full gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Upgrade to Basic or Pro
              <ChevronRight className="w-4 h-4" />
            </Button>
          )}

          {subscription?.tier === 'basic' && (
            <Button 
              onClick={() => navigate('/pricing')} 
              className="w-full gap-2"
            >
              <Crown className="w-4 h-4" />
              Upgrade to Pro
              <ChevronRight className="w-4 h-4" />
            </Button>
          )}

          {subscription?.tier !== 'free' && subscription?.tier !== 'lifetime_pro' && (
            <>
              <button
                onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/api/billing/portal`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (response.data.portal_url) {
                      window.location.href = response.data.portal_url;
                    } else if (response.data.is_test_mode) {
                      // Test mode - show specific message
                      toast.info('Billing portal is not available in test mode. Use cancel/reactivate options below.');
                    } else {
                      // Show info instead of error - user can still manage subscription here
                      toast.info(response.data.error || 'Use the options below to manage your subscription');
                    }
                  } catch (error) {
                    toast.info('Manage your subscription using the options below');
                  }
                }}
                className="w-full flex items-center justify-between p-4 bg-card rounded-xl border border-border hover:border-primary transition-colors"
              >
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-muted-foreground" />
                  <span>Manage Billing & Payment</span>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>

              {!subscription?.cancel_at_period_end ? (
                <>
                  {subscription?.status === 'trialing' && subscription?.trial_end ? (
                    <div className="text-center p-4 bg-blue-500/10 rounded-xl border border-blue-500/20 mb-4">
                      <p className="text-blue-600 dark:text-blue-400 text-sm">
                        Your first charge will be on {new Date(subscription.trial_end).toLocaleDateString()}, then auto-renews monthly
                      </p>
                    </div>
                  ) : subscription?.current_period_end && (
                    <div className="text-center p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20 mb-4">
                      <p className="text-emerald-600 dark:text-emerald-400 text-sm">
                        Your subscription will automatically renew on {new Date(subscription.current_period_end).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  <button
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to cancel your subscription? You will keep access until the end of your current billing period.')) {
                        try {
                          const response = await axios.post(`${API}/api/billing/cancel`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success(response.data.message);
                          fetchSubscription();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
                        }
                      }
                    }}
                    className="w-full flex items-center justify-center p-4 text-red-500 hover:bg-red-500/10 rounded-xl transition-colors"
                  >
                    Cancel Subscription
                  </button>
                </>
              ) : (
                <div className="text-center p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
                  {subscription?.current_period_end && (
                    <p className="text-yellow-600 dark:text-yellow-400 text-sm mb-2">
                      Your subscription will end on {new Date(subscription.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                  <button
                    onClick={async () => {
                      try {
                        const response = await axios.post(`${API}/api/billing/reactivate`, {}, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success(response.data.message);
                        fetchSubscription();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to reactivate');
                      }
                    }}
                    className="text-primary hover:underline text-sm font-medium"
                  >
                    Reactivate Subscription
                  </button>
                </div>
              )}
            </>
          )}

          {/* Lifetime Pro special message */}
          {subscription?.tier === 'lifetime_pro' && (
            <div className="text-center p-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 rounded-xl border border-amber-200 dark:border-amber-800">
              <Gift className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <h3 className="font-bold text-lg text-amber-900 dark:text-amber-100 mb-2">
                Lifetime Pro Member
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
                Thank you for being an early supporter! You have full Pro access forever with no recurring payments.
              </p>
              <div className="flex items-center justify-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                <Infinity className="w-4 h-4" />
                <span>Unlimited everything • All future features included</span>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
